import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { createStore } from '@ngneat/elf';
import { deleteEntities, selectAllEntities, selectEntity, setEntities, updateEntities, upsertEntities, withEntities } from '@ngneat/elf-entities';
import { tap, switchMap, first, shareReplay } from 'rxjs';

import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { Models } from '../../models/global.models';
import { WithId } from './store.models';

const store = createStore({ name: 'layouts' }, withEntities<WithId<Models.Layout, 'guid'>, 'guid'>({ idKey: 'guid' }));

/** Grid layouts store (for leads, loans, etc.) */
@Injectable({ providedIn: 'root' })
export class LayoutsRepository {
  private readonly _http = inject(HttpClient);
  private readonly _environment = inject(APP_ENV);

  private _baseUrl = this._environment.retail.apiUrl;

  all$ = this.get().pipe(shareReplay(1));

  reset() {
    store.reset();
  }

  get() {
    return this._http.get<WithId<Models.Layout, 'guid'>[]>(`${this._baseUrl}/layouts`).pipe(
      tap((layouts) => {
        store.update(setEntities(layouts));
      }),
      switchMap(() => store.pipe(selectAllEntities())),
    );
  }

  post(layout: Models.Layout) {
    return this._http.post<WithId<Models.Layout, 'guid'>>(`${this._baseUrl}/layouts`, layout).pipe(
      tap((response) => {
        store.update(upsertEntities(response));
      }),
    );
  }

  patch(layout: Models.Layout) {
    return this._http.patch(`${this._baseUrl}/layouts/${layout.guid}`, layout).pipe(
      tap(() => {
        store.update(updateEntities(layout.guid, (entity) => ({ ...entity, ...layout })));
      }),
      switchMap(() => store.pipe(selectEntity(layout.guid))),
      first(),
    );
  }

  delete(guid: string) {
    return this._http.delete(`${this._baseUrl}/layouts/${guid}`).pipe(
      tap(() => {
        store.update(deleteEntities([guid]));
      }),
    );
  }
}
